/**
 * Budget widget for dashboard - Shows current month budget status
 */

import { useMemo } from 'react';
import { TrendingDown, TrendingUp, AlertCircle, Wallet, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { BudgetProgressBar } from '../../../components/primitives';
import { formatCurrency } from '../../../lib/utils/format';
import { useCompanyCurrency } from '../../../hooks/useCompanyCurrency';
import { useBudgetTracking } from '../../../hooks/useBudgetTracking';
import { Skeleton } from '../../../components/ui/skeleton';
import { Button } from '../../../components/ui/button';
import { PermissionGuard } from '../../../components/permissions/PermissionGuard';

interface BudgetWidgetProps {
  companyIds: string[];
  userId: string;
  onNavigateToBudget?: () => void;
}

export function BudgetWidget({ companyIds, userId, onNavigateToBudget }: BudgetWidgetProps) {
  const companyCurrency = useCompanyCurrency();
  const { data, loading, hasActiveBudget } = useBudgetTracking(companyIds, userId);

  const projectionStatus = useMemo(() => {
    if (!data) return null;

    const willExceed = data.projectedEndOfMonth > data.totalBudgeted;
    const difference = Math.abs(data.projectedEndOfMonth - data.totalBudgeted);

    return {
      willExceed,
      difference,
      message: willExceed
        ? `Projeção indica ${formatCurrency(difference, companyCurrency)} acima do orçamento`
        : `Projeção dentro do orçamento (${formatCurrency(difference, companyCurrency)} de margem)`,
    };
  }, [data]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-4 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!hasActiveBudget || !data) {
    return (
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle>Orçamento do Mês</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center space-y-4">
            {/* Icon with gradient background */}
            <div className="relative">
              <div className="absolute inset-0 bg-primary/10 blur-2xl rounded-full" />
              <div className="relative bg-primary/5 p-4 rounded-2xl border border-primary/20">
                <Wallet className="h-10 w-10 text-primary" />
              </div>
            </div>

            {/* Text */}
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground max-w-[240px]">
                Controle seus gastos e mantenha suas finanças organizadas
              </p>
            </div>

            {/* CTA */}
            <PermissionGuard check="canCreate" module="budgets">
              <Button 
                size="default" 
                className="mt-2 gap-2" 
                onClick={onNavigateToBudget}
              >
                <Plus className="h-4 w-4" />
                Criar Orçamento
              </Button>
            </PermissionGuard>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { totalBudgeted, totalSpent, totalRemaining, percentUsed, status } = data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Orçamento do Mês</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Main Progress */}
        <div className="space-y-2">
          <BudgetProgressBar
            current={totalSpent}
            total={totalBudgeted}
            showLabel
            showIcon
            size="lg"
          />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <div className="space-y-1.5 p-3 rounded-lg bg-muted/30 border">
            <p className="text-xs text-muted-foreground">Disponível</p>
            <p className={`font-medium text-base md:text-lg truncate ${totalRemaining >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
              {formatCurrency(Math.max(0, totalRemaining), companyCurrency)}
            </p>
          </div>
          <div className="space-y-1.5 p-3 rounded-lg bg-muted/30 border">
            <p className="text-xs text-muted-foreground">Dias restantes</p>
            <p className="font-medium text-base md:text-lg">{data.daysRemaining} dias</p>
          </div>
        </div>

        {/* Projection */}
        {projectionStatus && (
          <div
            className={`rounded-lg border p-3 ${
              projectionStatus.willExceed
                ? 'border-warning-200 bg-warning-50 dark:border-warning-800 dark:bg-warning-950'
                : 'border-success-200 bg-success-50 dark:border-success-800 dark:bg-success-950'
            }`}
          >
            <div className="flex items-start gap-2">
              {projectionStatus.willExceed ? (
                <TrendingUp className="h-4 w-4 text-warning-600 dark:text-warning-400 mt-0.5 shrink-0" />
              ) : (
                <TrendingDown className="h-4 w-4 text-success-600 dark:text-success-400 mt-0.5 shrink-0" />
              )}
              <div className="flex-1 min-w-0 space-y-1">
                <p className="text-xs">Projeção fim do mês</p>
                <p
                  className={`text-sm truncate ${
                    projectionStatus.willExceed
                      ? 'text-warning-700 dark:text-warning-300'
                      : 'text-success-700 dark:text-success-300'
                  }`}
                >
                  {formatCurrency(data.projectedEndOfMonth, companyCurrency)}
                </p>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {projectionStatus.message}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Link to full budget */}
        <Button variant="outline" size="sm" className="w-full" onClick={onNavigateToBudget}>
          Ver orçamento completo
        </Button>
      </CardContent>
    </Card>
  );
}
